import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LuArrowLeft, LuBan, LuCircleCheck, LuClock, LuCopy, LuFacebook, LuHand, LuMapPin,
  LuMessageCircle, LuMessagesSquare, LuPackageCheck, LuPencil, LuPhone, LuPlus, LuRefreshCw,
  LuSend, LuSettings, LuShoppingBag, LuSmartphone, LuStore, LuTriangleAlert, LuTruck, LuWallet, LuZap,
} from 'react-icons/lu';
import API from '../../api/axios';
import { Badge, Button, Card, EmptyState, Grid, Loader, StatCard, Tabs } from '../ui';
import { C, fmtF } from '../../theme';
import './CommandesEnLigne.css';

// ── Libellés ─────────────────────────────────────────────────────
const STATUTS = {
  confirmee:      { label: 'Nouvelle',       tone: 'info' },
  en_preparation: { label: 'En préparation', tone: 'purple' },
  en_livraison:   { label: 'En livraison',   tone: 'warn' },
  livree:         { label: 'Livrée',         tone: 'success' },
  annulee:        { label: 'Annulée',        tone: 'danger' },
};
const PAIEMENTS = {
  a_la_livraison: 'À la livraison',
  mobile_money: 'Mobile Money',
  sur_place: 'Sur place',
};
const ETAPE_SUIVANTE = {
  confirmee:      { statut: 'en_preparation', label: 'Préparer', icon: LuPackageCheck },
  en_preparation: { statut: 'en_livraison',   label: 'Expédier', labelRetrait: 'Prête', icon: LuTruck },
  en_livraison:   { statut: 'livree',         label: 'Livrée',   labelRetrait: 'Remise', icon: LuCircleCheck },
};
const CANAUX = {
  whatsapp:  { label: 'WhatsApp',  couleur: '#16A34A', icon: LuMessageCircle },
  messenger: { label: 'Messenger', couleur: '#2563EB', icon: LuFacebook },
};

const heure = d => new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const msgErreur = e => e?.response?.data?.message?.replace(/^❌\s*/, '') || 'Erreur réseau';

function CanalBadge({ canal }) {
  const c = CANAUX[canal] || CANAUX.whatsapp;
  const Icon = c.icon;
  return (
    <span className="cel-canal" style={{ color: c.couleur, backgroundColor: `color-mix(in srgb, ${c.couleur} 10%, white)` }}>
      <Icon aria-hidden /> {c.label}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════
/** `admin` : l'administrateur StockPro voit en plus l'onglet Configuration (canaux, clés, réglages). */
export default function CommandesEnLigne({ etablissements = [], onResume, admin = false }) {
  const [vue, setVue] = useState(admin ? 'configuration' : 'commandes');
  const [resume, setResume] = useState(null);
  const [toast, setToast] = useState('');
  const [conversationOuverte, setConversationOuverte] = useState(null);

  const notifier = useCallback(t => { setToast(t); setTimeout(() => setToast(''), 3500); }, []);

  const chargerResume = useCallback(async () => {
    try {
      const r = await API.get('/commandes-en-ligne/resume');
      setResume(r.data);
      onResume?.(r.data);
    } catch (_) { /* silencieux */ }
  }, [onResume]);

  useEffect(() => {
    chargerResume();
    const t = setInterval(chargerResume, 15000);
    return () => clearInterval(t);
  }, [chargerResume]);

  const ouvrirConversation = id => { setConversationOuverte(id); setVue('conversations'); };
  const aTraiter = Number(resume?.non_lus || 0);

  return (
    <div className="cel">
      {toast && <div role="status" className={`cel-toast ${toast.startsWith('❌') ? 'is-err' : ''}`}>{toast.replace(/^(✅|❌)\s*/, '')}</div>}

      <Tabs
        tabs={[
          { id: 'commandes', label: `Commandes${Number(resume?.nouvelles) ? ` (${resume.nouvelles})` : ''}`, icon: LuShoppingBag },
          { id: 'conversations', label: `Conversations${aTraiter ? ` (${aTraiter})` : ''}`, icon: LuMessagesSquare },
          ...(admin ? [{ id: 'configuration', label: 'Configuration', icon: LuSettings }] : []),
        ]}
        value={vue}
        onChange={setVue}
      />

      {vue === 'commandes' && (
        <VueCommandes resume={resume} onChange={chargerResume} notifier={notifier} ouvrirConversation={ouvrirConversation} />
      )}
      {vue === 'conversations' && (
        <VueConversations selection={conversationOuverte} setSelection={setConversationOuverte} onChange={chargerResume} notifier={notifier} />
      )}
      {admin && vue === 'configuration' && <VueConfiguration etablissements={etablissements} notifier={notifier} />}
    </div>
  );
}

// ════════════════════════ COMMANDES ════════════════════════
function VueCommandes({ resume, onChange, notifier, ouvrirConversation }) {
  const [commandes, setCommandes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [filtre, setFiltre] = useState('actives');
  const [annulation, setAnnulation] = useState(null); // { id, motif }
  const [action, setAction] = useState(null);

  const charger = useCallback(async () => {
    try {
      const r = await API.get('/commandes-en-ligne');
      setCommandes(r.data.commandes || []);
    } catch (e) { notifier('❌ ' + msgErreur(e)); }
    finally { setChargement(false); }
  }, [notifier]);

  useEffect(() => {
    charger();
    const t = setInterval(charger, 15000);
    return () => clearInterval(t);
  }, [charger]);

  const visibles = useMemo(() => commandes.filter(c => {
    if (filtre === 'actives') return ['confirmee', 'en_preparation', 'en_livraison'].includes(c.statut);
    if (filtre === 'toutes') return true;
    return c.statut === filtre;
  }), [commandes, filtre]);

  const executer = async (id, requete, succes) => {
    setAction(id);
    try {
      await requete();
      notifier('✅ ' + succes);
      await Promise.all([charger(), onChange()]);
    } catch (e) { notifier('❌ ' + msgErreur(e)); }
    finally { setAction(null); }
  };

  const avancer = c => {
    const e = ETAPE_SUIVANTE[c.statut];
    executer(c.id, () => API.patch(`/commandes-en-ligne/${c.id}/statut`, { statut: e.statut }), 'Statut mis à jour, client prévenu');
  };
  const payer = c => executer(c.id, () => API.patch(`/commandes-en-ligne/${c.id}/paiement`, {}), 'Paiement enregistré');
  const annuler = () => {
    const { id, motif } = annulation;
    setAnnulation(null);
    executer(id, () => API.patch(`/commandes-en-ligne/${id}/statut`, { statut: 'annulee', motif }), 'Commande annulée, stock remis');
  };

  if (chargement) return <Loader fullScreen={false} />;

  return (
    <>
      <Grid min={150} style={{ marginBottom: 20 }}>
        <StatCard icon={LuShoppingBag} label="Nouvelles commandes" value={resume?.nouvelles ?? 0} color={C.info} />
        <StatCard icon={LuTruck} label="En cours" value={resume?.en_cours ?? 0} color={C.purple} />
        <StatCard icon={LuWallet} label="Revenus en ligne du jour" value={`${fmtF(resume?.ca_du_jour)}`} hint={`${resume?.du_jour ?? 0} commande(s)`} color={C.success} />
        <StatCard icon={LuSmartphone} label="Mobile Money à vérifier" value={resume?.paiements_attente ?? 0} color={C.warn} />
      </Grid>

      <div className="cel-filtres" role="group" aria-label="Filtrer les commandes">
        {[['actives', 'En cours'], ['confirmee', 'Nouvelles'], ['livree', 'Livrées'], ['annulee', 'Annulées'], ['toutes', 'Toutes']].map(([id, label]) => (
          <button key={id} className="cel-chip" aria-pressed={filtre === id} onClick={() => setFiltre(id)}>{label}</button>
        ))}
      </div>

      {visibles.length === 0 ? (
        <Card><EmptyState icon={LuShoppingBag} title="Aucune commande" text="Les commandes reçues sur WhatsApp et Messenger apparaîtront ici." /></Card>
      ) : (
        <div className="cel-liste">
          {visibles.map(c => {
            const etape = ETAPE_SUIVANTE[c.statut];
            const retrait = c.mode_reception === 'retrait';
            const momoAttente = c.mode_paiement === 'mobile_money' && c.statut_paiement === 'en_attente' && c.statut !== 'annulee';
            return (
              <article key={c.id} className={`cel-commande ${c.statut === 'confirmee' ? 'is-new' : ''}`}>
                <header className="cel-commande-tete">
                  <div>
                    <div className="cel-commande-num">Commande n°{c.id}</div>
                    <div className="cel-sub">{heure(c.created_at)}{c.etablissement_nom ? ` · ${c.etablissement_nom}` : ''}</div>
                  </div>
                  <div className="cel-badges">
                    <CanalBadge canal={c.canal} />
                    <Badge tone={STATUTS[c.statut]?.tone}>{STATUTS[c.statut]?.label}</Badge>
                  </div>
                </header>

                <div className="cel-client">
                  <strong>{c.client_nom || 'Client'}</strong>
                  {c.client_telephone && <a href={`tel:${c.client_telephone}`} className="cel-lien"><LuPhone aria-hidden /> {c.client_telephone}</a>}
                  <span className="cel-sub">
                    {retrait ? <><LuStore aria-hidden /> Retrait sur place</> : <><LuMapPin aria-hidden /> {c.adresse_livraison}</>}
                  </span>
                </div>

                <ul className="cel-articles">
                  {(c.articles || []).map((a, i) => (
                    <li key={i}>
                      <span><span className="cel-qte">{a.quantite}×</span> {a.nom}{a.type === 'kit' && <span className="cel-sub" style={{ marginLeft: 4 }}>(kit)</span>}</span>
                      <span className="cel-num">{fmtF(a.prix_unitaire * a.quantite)}</span>
                    </li>
                  ))}
                  {Number(c.frais_livraison) > 0 && <li className="cel-sub"><span>Frais de livraison</span><span className="cel-num">{fmtF(c.frais_livraison)}</span></li>}
                  <li className="cel-total"><span>Total</span><span className="cel-num">{fmtF(c.montant_total)}</span></li>
                </ul>

                <div className="cel-paiement">
                  <span>{PAIEMENTS[c.mode_paiement]}</span>
                  <Badge tone={c.statut_paiement === 'paye' ? 'success' : momoAttente ? 'warn' : 'neutral'}>
                    {c.statut_paiement === 'paye' ? 'Payé' : 'Non payé'}
                  </Badge>
                </div>
                {c.note && <p className="cel-note">« {c.note} »</p>}
                {c.motif_annulation && <p className="cel-note">Motif : {c.motif_annulation}</p>}

                {annulation?.id === c.id ? (
                  <div className="cel-annulation">
                    <input
                      className="cel-input" autoFocus placeholder="Motif (envoyé au client)"
                      value={annulation.motif} onChange={e => setAnnulation({ ...annulation, motif: e.target.value })}
                    />
                    <Button variant="danger" onClick={annuler}>Confirmer l'annulation</Button>
                    <Button variant="secondary" onClick={() => setAnnulation(null)}>Retour</Button>
                  </div>
                ) : (
                  <footer className="cel-actions">
                    {etape && (
                      <Button icon={etape.icon} disabled={action === c.id} onClick={() => avancer(c)}>
                        {retrait && etape.labelRetrait ? etape.labelRetrait : etape.label}
                      </Button>
                    )}
                    {momoAttente && <Button variant="secondary" icon={LuWallet} disabled={action === c.id} onClick={() => payer(c)}>Paiement reçu</Button>}
                    {c.conversation_id && <Button variant="secondary" icon={LuMessagesSquare} onClick={() => ouvrirConversation(c.conversation_id)}>Conversation</Button>}
                    {!['annulee', 'livree', 'en_livraison'].includes(c.statut) && (
                      <Button variant="danger" icon={LuBan} disabled={action === c.id} onClick={() => setAnnulation({ id: c.id, motif: '' })}>Annuler</Button>
                    )}
                  </footer>
                )}
              </article>
            );
          })}
        </div>
      )}
    </>
  );
}

// ════════════════════════ CONVERSATIONS ════════════════════════
function VueConversations({ selection, setSelection, onChange, notifier }) {
  const [liste, setListe] = useState([]);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    try {
      const r = await API.get('/conversations');
      setListe(r.data.conversations || []);
    } catch (e) { notifier('❌ ' + msgErreur(e)); }
    finally { setChargement(false); }
  }, [notifier]);

  useEffect(() => {
    charger();
    const t = setInterval(charger, 10000);
    return () => clearInterval(t);
  }, [charger]);

  if (chargement) return <Loader fullScreen={false} />;
  if (!liste.length) {
    return <Card><EmptyState icon={LuMessagesSquare} title="Aucune conversation" text="Dès qu'un client écrira sur WhatsApp ou Messenger, la discussion apparaîtra ici." /></Card>;
  }

  return (
    <div className={`cel-messagerie ${selection ? 'has-selection' : ''}`}>
      <aside className="cel-convs" aria-label="Conversations">
        {liste.map(cv => (
          <button key={cv.id} className="cel-conv" aria-current={selection === cv.id ? 'true' : undefined} onClick={() => setSelection(cv.id)}>
            <div className="cel-conv-avatar" style={{ backgroundColor: CANAUX[cv.canal]?.couleur }}>
              {(cv.client_nom || '?').trim()[0]?.toUpperCase()}
            </div>
            <div className="cel-conv-corps">
              <div className="cel-conv-ligne">
                <strong>{cv.client_nom || cv.client_telephone || 'Client'}</strong>
                <span className="cel-sub">{heure(cv.derniere_activite)}</span>
              </div>
              <div className="cel-conv-ligne">
                <span className="cel-conv-apercu">{cv.dernier_message || '…'}</span>
                {cv.non_lus > 0 && <span className="cel-pastille">{cv.non_lus}</span>}
              </div>
              <div className="cel-conv-tags">
                <CanalBadge canal={cv.canal} />
                {cv.mode === 'humain'
                  ? <Badge tone="warn"><LuHand aria-hidden /> Vous</Badge>
                  : <Badge tone="info"><LuZap aria-hidden /> Auto</Badge>}
              </div>
            </div>
          </button>
        ))}
      </aside>

      <section className="cel-fil">
        {selection
          ? <FilConversation key={selection} id={selection} retour={() => setSelection(null)} onChange={() => { charger(); onChange(); }} notifier={notifier} />
          : <EmptyState icon={LuMessageCircle} title="Sélectionnez une conversation" />}
      </section>
    </div>
  );
}

function FilConversation({ id, retour, onChange, notifier }) {
  const [donnees, setDonnees] = useState(null);
  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const bas = useRef(null);
  const nbMessages = useRef(0);

  const charger = useCallback(async () => {
    try {
      const r = await API.get(`/conversations/${id}`);
      setDonnees(r.data);
    } catch (e) { notifier('❌ ' + msgErreur(e)); }
  }, [id, notifier]);

  useEffect(() => {
    charger().then(onChange);
    const t = setInterval(charger, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charger]);

  useEffect(() => {
    const n = donnees?.messages?.length || 0;
    if (n !== nbMessages.current) {
      nbMessages.current = n;
      // défile uniquement la zone des messages (pas toute la page)
      if (bas.current) bas.current.scrollTop = bas.current.scrollHeight;
    }
  }, [donnees]);

  if (!donnees) return <Loader fullScreen={false} />;
  const { conversation: cv, messages, commandes } = donnees;
  const humain = cv.mode === 'humain';

  const envoyer = async e => {
    e.preventDefault();
    if (!texte.trim()) return;
    setEnvoi(true);
    try {
      await API.post(`/conversations/${id}/messages`, { contenu: texte.trim() });
      setTexte('');
      await charger();
      onChange();
    } catch (err) { notifier('❌ ' + msgErreur(err)); }
    finally { setEnvoi(false); }
  };

  const basculer = async () => {
    try {
      const r = await API.patch(`/conversations/${id}/mode`, { mode: humain ? 'agent' : 'humain' });
      notifier(r.data.message);
      await charger();
      onChange();
    } catch (err) { notifier('❌ ' + msgErreur(err)); }
  };

  return (
    <div className="cel-fil-inner">
      <header className="cel-fil-tete">
        <button className="cel-retour" onClick={retour} aria-label="Retour à la liste"><LuArrowLeft aria-hidden /></button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="cel-fil-nom">{cv.client_nom || 'Client'}</div>
          <div className="cel-sub">
            <CanalBadge canal={cv.canal} />{' '}
            {cv.client_telephone && <a className="cel-lien" href={`tel:${cv.client_telephone}`}>{cv.client_telephone}</a>}
          </div>
        </div>
        <Button variant={humain ? 'primary' : 'secondary'} icon={humain ? LuZap : LuHand} onClick={basculer}>
          {humain ? 'Réactiver les réponses auto' : 'Prendre la main'}
        </Button>
      </header>

      <div className={`cel-mode ${humain ? 'is-humain' : ''}`}>
        {humain
          ? <><LuHand aria-hidden /> Vous répondez à ce client : les réponses automatiques sont en pause sur cette conversation.</>
          : <><LuZap aria-hidden /> Les réponses au client sont automatiques. Écrire un message vous donne la main.</>}
      </div>

      {(cv.panier?.length > 0 || commandes.length > 0) && (
        <div className="cel-contexte">
          {cv.panier?.length > 0 && (
            <span><LuShoppingBag aria-hidden /> Panier en cours : {cv.panier.map(l => `${l.quantite}× ${l.nom}`).join(', ')}</span>
          )}
          {commandes.slice(0, 3).map(c => (
            <span key={c.id}>n°{c.id} · {fmtF(c.montant_total)} · <Badge tone={STATUTS[c.statut]?.tone}>{STATUTS[c.statut]?.label}</Badge></span>
          ))}
        </div>
      )}

      <div className="cel-messages" ref={bas}>
        {messages.map(m => (
          m.auteur === 'systeme'
            ? <div key={m.id} className="cel-msg-systeme">{m.contenu}</div>
            : (
              <div key={m.id} className={`cel-msg cel-msg-${m.auteur}`}>
                {m.auteur !== 'client' && (
                  <div className="cel-msg-auteur">{m.auteur === 'agent' ? <><LuZap aria-hidden /> Réponse automatique</> : <><LuHand aria-hidden /> {m.utilisateur_prenom || 'Depuis l’application WhatsApp'}</>}</div>
                )}
                <div className="cel-msg-texte">{m.contenu}</div>
                <div className="cel-msg-heure">{heure(m.created_at)}</div>
              </div>
            )
        ))}
      </div>

      <form className="cel-saisie" onSubmit={envoyer}>
        <textarea
          className="cel-input" rows={2} placeholder="Écrire au client…" value={texte}
          onChange={e => setTexte(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) envoyer(e); }}
        />
        <Button type="submit" icon={LuSend} disabled={envoi || !texte.trim()} aria-label="Envoyer">Envoyer</Button>
      </form>
    </div>
  );
}

// ════════════════════════ CONFIGURATION ════════════════════════
const VIDE = {
  type: 'whatsapp', identifiant: '', access_token: '', nom_affiche: '', numero_mobile_money: '',
  zone_livraison: '', frais_livraison: '', consignes_agent: '', etablissement_id: '',
  fournisseur_api: '360dialog', delai_reprise_min: 120,
};

function VueConfiguration({ etablissements, notifier }) {
  const [donnees, setDonnees] = useState(null);
  const [form, setForm] = useState(null); // null = fermé ; { id? , ...champs }
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await API.get('/canaux');
      setDonnees(r.data);
    } catch (e) { notifier('❌ ' + msgErreur(e)); }
  }, [notifier]);

  useEffect(() => { charger(); }, [charger]);

  if (!donnees) return <Loader fullScreen={false} />;

  const d360 = form && form.type === 'whatsapp' && form.fournisseur_api === '360dialog';

  const reconfigurer = async c => {
    try {
      const r = await API.post(`/canaux/${c.id}/webhook`);
      notifier(r.data.message);
    } catch (err) { notifier('❌ ' + msgErreur(err)); }
  };

  const champ = (nom, label, props = {}) => (
    <label className="cel-champ">
      <span>{label}</span>
      {props.textarea
        ? <textarea className="cel-input" rows={3} value={form[nom] ?? ''} onChange={e => setForm({ ...form, [nom]: e.target.value })} placeholder={props.placeholder} />
        : <input className="cel-input" value={form[nom] ?? ''} onChange={e => setForm({ ...form, [nom]: e.target.value })} {...props} />}
    </label>
  );

  const enregistrer = async e => {
    e.preventDefault();
    setEnvoi(true);
    try {
      const corps = { ...form };
      if (form.id && !corps.access_token) delete corps.access_token;
      const r = form.id ? await API.put(`/canaux/${form.id}`, corps) : await API.post('/canaux', corps);
      notifier(r.data.message);
      setForm(null);
      charger();
    } catch (err) { notifier('❌ ' + msgErreur(err)); }
    finally { setEnvoi(false); }
  };

  const basculerActif = async c => {
    try {
      await API.put(`/canaux/${c.id}`, { actif: !c.actif });
      notifier(c.actif ? '✅ Canal désactivé' : '✅ Canal réactivé');
      charger();
    } catch (err) { notifier('❌ ' + msgErreur(err)); }
  };

  const copier = async t => {
    try { await navigator.clipboard.writeText(t); notifier('✅ Copié'); } catch (_) { /* ignore */ }
  };

  return (
    <>
      {(!donnees.ia_configuree || !donnees.meta_configure) && (
        <div className="cel-alerte">
          <LuTriangleAlert aria-hidden />
          <div>
            {!donnees.ia_configuree && <div>La clé de l'IA (<code>ANTHROPIC_API_KEY</code>) n'est pas configurée sur le serveur.</div>}
            {!donnees.meta_configure && <div>Les paramètres Meta (<code>META_VERIFY_TOKEN</code>, <code>META_APP_SECRET</code>) ne sont pas configurés sur le serveur.</div>}
          </div>
        </div>
      )}

      <Card title="URL du webhook Meta" style={{ marginBottom: 16 }}>
        <div className="cel-webhook">
          <code>{donnees.webhook_url}</code>
          <Button variant="secondary" icon={LuCopy} onClick={() => copier(donnees.webhook_url)}>Copier</Button>
        </div>
        <p className="cel-sub" style={{ marginTop: 8 }}>
          Pour Messenger et pour WhatsApp en API Meta directe : à coller dans votre application Meta (WhatsApp → Configuration, Messenger → Webhooks), avec le jeton de vérification défini dans <code>META_VERIFY_TOKEN</code>. Les canaux WhatsApp en coexistence (360dialog) ont leur propre URL, affichée sous le canal.
        </p>
      </Card>

      <Card
        title="Canaux connectés"
        action={!form && <Button icon={LuPlus} onClick={() => setForm({ ...VIDE, etablissement_id: etablissements[0]?.id || '' })}>Connecter un canal</Button>}
      >
        {form && (
          <form className="cel-form" onSubmit={enregistrer}>
            <div className="cel-form-grid">
              {!form.id && (
                <label className="cel-champ">
                  <span>Canal</span>
                  <select className="cel-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                    <option value="whatsapp">WhatsApp Business</option>
                    <option value="messenger">Messenger (Page Facebook)</option>
                  </select>
                </label>
              )}
              {!form.id && etablissements.length > 1 && (
                <label className="cel-champ">
                  <span>Établissement</span>
                  <select className="cel-input" value={form.etablissement_id} onChange={e => setForm({ ...form, etablissement_id: e.target.value })}>
                    {etablissements.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
                  </select>
                </label>
              )}
              {!form.id && form.type === 'whatsapp' && (
                <label className="cel-champ">
                  <span>Connexion WhatsApp</span>
                  <select className="cel-input" value={form.fournisseur_api} onChange={e => setForm({ ...form, fournisseur_api: e.target.value })}>
                    <option value="360dialog">Coexistence via 360dialog (garde l'application WhatsApp)</option>
                    <option value="meta">API Meta directe (numéro dédié)</option>
                  </select>
                </label>
              )}
              {champ('nom_affiche', 'Nom affiché', { placeholder: 'Ex : WhatsApp BabySpirit' })}
              {d360
                ? champ('identifiant', 'Numéro WhatsApp', { required: !form.id, placeholder: 'Ex : 225 07 07 07 07 07', inputMode: 'tel' })
                : champ('identifiant', form.type === 'whatsapp' ? 'Phone number ID (Meta)' : 'ID de la Page Facebook', { required: !form.id, placeholder: 'Ex : 109876543210987' })}
              {champ('access_token',
                d360 ? (form.id ? 'Clé API 360dialog (laisser vide pour garder)' : 'Clé API 360dialog')
                  : (form.id ? "Jeton d'accès (laisser vide pour garder)" : "Jeton d'accès permanent"),
                { required: !form.id, type: 'password', autoComplete: 'off' })}
              {champ('numero_mobile_money', 'Numéro Mobile Money', { placeholder: 'Ex : Wave 07 07 07 07 07 (Awa Koné)' })}
              {champ('frais_livraison', 'Frais de livraison (FCFA)', { type: 'number', min: 0, inputMode: 'numeric' })}
              {champ('zone_livraison', 'Zone de livraison', { placeholder: 'Ex : Cocody, Riviera, Angré' })}
              {champ('delai_reprise_min', "Reprise par l'agent après (minutes sans réponse humaine, 0 = jamais)", { type: 'number', min: 0, inputMode: 'numeric' })}
            </div>
            {champ('consignes_agent', "Consignes pour l'agent IA", { textarea: true, placeholder: 'Ex : Pas de livraison le dimanche. Toujours proposer les lingettes avec les couches.' })}
            <div className="cel-actions">
              <Button type="submit" disabled={envoi}>{form.id ? 'Enregistrer' : 'Connecter'}</Button>
              <Button type="button" variant="secondary" onClick={() => setForm(null)}>Annuler</Button>
            </div>
          </form>
        )}

        {!form && (donnees.canaux.length === 0 ? (
          <EmptyState icon={LuMessageCircle} title="Aucun canal connecté" text="Connectez le WhatsApp Business ou la page Facebook de l'établissement pour activer l'agent IA." />
        ) : (
          <div className="cel-canaux">
            {donnees.canaux.map(c => (
              <div key={c.id} className="cel-canal-ligne">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="cel-badges">
                    <CanalBadge canal={c.type} />
                    {c.fournisseur_api === '360dialog' && <Badge tone="purple">Coexistence · 360dialog</Badge>}
                    <Badge tone={c.actif ? 'success' : 'neutral'}>{c.actif ? 'Actif' : 'Désactivé'}</Badge>
                  </div>
                  <div className="cel-commande-num" style={{ marginTop: 6 }}>{c.nom_affiche || c.etablissement_nom}</div>
                  <div className="cel-sub">{c.fournisseur_api === '360dialog' ? `+${c.identifiant}` : `ID ${c.identifiant}`} · clé {c.token_masque} · livraison {fmtF(c.frais_livraison)}</div>
                  <div className="cel-sub">Reprise par l'agent : {Number(c.delai_reprise_min) > 0 ? `après ${c.delai_reprise_min} min sans réponse humaine` : 'jamais (manuelle)'}</div>
                  {c.numero_mobile_money && <div className="cel-sub">Mobile Money : {c.numero_mobile_money}</div>}
                  {c.webhook_url_canal && (
                    <div className="cel-webhook" style={{ marginTop: 8 }}>
                      <code>{c.webhook_url_canal}</code>
                      <Button variant="secondary" icon={LuCopy} onClick={() => copier(c.webhook_url_canal)}>Copier</Button>
                      <Button variant="secondary" icon={LuRefreshCw} onClick={() => reconfigurer(c)}>Déclarer à 360dialog</Button>
                    </div>
                  )}
                </div>
                <div className="cel-actions">
                  <Button variant="secondary" icon={LuPencil} onClick={() => setForm({ ...VIDE, ...c, access_token: '', identifiant: '', frais_livraison: Number(c.frais_livraison) })}>Modifier</Button>
                  <Button variant={c.actif ? 'danger' : 'secondary'} icon={c.actif ? LuBan : LuRefreshCw} onClick={() => basculerActif(c)}>{c.actif ? 'Désactiver' : 'Réactiver'}</Button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </Card>

      <p className="cel-sub" style={{ marginTop: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
        <LuClock aria-hidden /> Les commandes confirmées par le client sont enregistrées automatiquement comme ventes et le stock est déduit aussitôt.
      </p>
    </>
  );
}
